import { Component, computed, effect, inject, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CombinedCampaignSelectorService } from '../../../../../data-services/combined-campaign-selector.service';
import { StaticSelectorService } from '../../../../../data-services/static-selector.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { And, Comparison, IsOneOfInStringListFold, Rule, StringExists } from '@vendasta/automata';
import {
  CampaignOwner,
  CampaignOwnerType,
  CrmEmailActivityCampaignIDPath,
  CrmEmailActivityStatusPath,
  EmailStatus,
  EmailStatusType,
  GetEmailStatusOptions,
  MarketingEmailStatus,
  YeswareEmailStatus,
} from './constants';

@Component({
  selector: 'automata-crm-email-activity-selector',
  imports: [
    CommonModule,
    TranslateModule,
    AutomationReplacementChipsComponent,
    ReactiveFormsModule,
    GalaxyFormFieldModule,
  ],
  templateUrl: './crm-email-activity-selector.component.html',
})
export class CrmEmailActivitySelectorComponent extends FormGroup {
  translateService = inject(TranslateService);

  campaignSelectorService = inject(CombinedCampaignSelectorService);
  campaignIdsFormControl: FormControl<string[]>;

  emailStatusSelectorService = computed(() => {
    const campaignType = this.campaignType();
    if (!campaignType) {
      return null;
    }
    return new StaticSelectorService(GetEmailStatusOptions(campaignType), this.translateService);
  });
  emailStatusFormControl: FormControl<string[]>;

  campaignIds: Signal<string[]>;
  campaignType = signal<CampaignOwnerType | null>(null);
  emailStatus: Signal<string[]>;

  constructor() {
    const campaignIdsFormControl = new FormControl<string[]>([], { nonNullable: true });
    const emailStatusFormControl = new FormControl<EmailStatusType[]>([], { nonNullable: true });
    super({
      campaignIds: campaignIdsFormControl,
      emailStatus: emailStatusFormControl,
    });
    this.campaignIdsFormControl = campaignIdsFormControl;
    this.emailStatusFormControl = emailStatusFormControl;

    this.campaignIds = toSignal(this.campaignIdsFormControl.valueChanges, { initialValue: [] });
    this.emailStatus = toSignal(this.emailStatusFormControl.valueChanges, { initialValue: [] });

    effect(() => {
      const selectedCampaignIds = this.campaignIds();
      this.handleSelectedCampaignIdsChanged(selectedCampaignIds);
    });
    effect(() => {
      const selectedEmailStatuses = this.emailStatus();
      this.handleSelectedEmailStatusChanged(selectedEmailStatuses);
    });
    effect(() => {
      const campaignType = this.campaignType();
      this.handleCampaignTypeChanged(campaignType);
    });
  }

  public getRule(): Rule {
    const selectedCampaignIds = this.campaignIdsFormControl.value || [];
    let selectedEmailStatus = this.emailStatusFormControl.value || [];

    if (!selectedCampaignIds.length && !selectedEmailStatus.length) {
      return new And([
        new StringExists(CrmEmailActivityCampaignIDPath, ''),
        new StringExists(CrmEmailActivityStatusPath, ''),
      ]);
    }

    if (selectedEmailStatus.includes(EmailStatus.CombinedSent)) {
      selectedEmailStatus = selectedEmailStatus.filter((status) => status !== EmailStatus.CombinedSent);
      selectedEmailStatus.push(MarketingEmailStatus.Sent, YeswareEmailStatus.Send);
    }

    const campaignRule = selectedCampaignIds.includes(CampaignOwner.Any)
      ? new StringExists(CrmEmailActivityCampaignIDPath, '')
      : new IsOneOfInStringListFold(CrmEmailActivityCampaignIDPath, selectedCampaignIds);

    const statusRule = selectedEmailStatus.includes(EmailStatus.Any)
      ? new StringExists(CrmEmailActivityStatusPath, '')
      : new IsOneOfInStringListFold(CrmEmailActivityStatusPath, selectedEmailStatus);

    return new And([campaignRule, statusRule]);
  }

  public setByRule(rule: Rule): void {
    if (
      !Array.isArray(rule.value) ||
      rule.value.length !== 2 ||
      (!rule.getVariablePath().includes(CrmEmailActivityCampaignIDPath) &&
        !rule.getVariablePath().includes(CrmEmailActivityStatusPath))
    ) {
      return;
    }

    const getRuleValue = (rule: Rule): string[] => {
      switch (rule.comparison) {
        case Comparison.StringExists:
          return ['*'];
        case Comparison.IsOneOfInStringListFold:
          return rule.value as string[];
        default:
          return [];
      }
    };

    const campaignIdValues = getRuleValue(rule.value[0] as Rule);
    let statusValues = getRuleValue(rule.value[1] as Rule);

    if (statusValues.includes(YeswareEmailStatus.Send) && statusValues.includes(MarketingEmailStatus.Sent)) {
      statusValues = statusValues.filter(
        (status) => status !== YeswareEmailStatus.Send && status !== MarketingEmailStatus.Sent,
      );
      statusValues.push(EmailStatus.CombinedSent);
    }

    this.patchValue({
      campaignIds: campaignIdValues,
      emailStatus: statusValues,
    });
  }

  private async getCampaignType(selectedCampaignIds?: string[]): Promise<CampaignOwnerType | null> {
    if (!selectedCampaignIds?.length) {
      return null;
    }

    try {
      const campaigns = await firstValueFrom(this.campaignSelectorService.getMulti(selectedCampaignIds));
      const campaignTypes = new Set(campaigns.map((campaign) => (campaign.id != '*' ? campaign.source : '*')));
      if (campaignTypes.size === 1) {
        const campaignType = campaignTypes.values().next().value;
        switch (campaignType) {
          case 'yesware':
            return CampaignOwner.Yesware;
          case 'campaigns':
            return CampaignOwner.Marketing;
          default:
            return CampaignOwner.Any;
        }
      } else {
        return CampaignOwner.Any;
      }
    } catch (error) {
      console.error('Error fetching campaign type:', error);
      return null;
    }
  }

  private handleWildCardInput(input: string[]): string[] {
    if (input.length > 1 && input.includes('*')) {
      const mostRecentInput = input[input.length - 1];
      if (mostRecentInput === '*') {
        return ['*'];
      } else {
        return input.filter((id) => id !== '*');
      }
    }
    return input;
  }

  private handleSelectedCampaignIdsChanged(selectedCampaignIds: string[]): void {
    if (!selectedCampaignIds?.length) {
      this.emailStatusFormControl.reset();
    }

    const formattedCampaignIds = this.handleWildCardInput(selectedCampaignIds);
    if (selectedCampaignIds?.length != formattedCampaignIds?.length) {
      this.campaignIdsFormControl.setValue(formattedCampaignIds);
      return;
    }
    this.getCampaignType(selectedCampaignIds).then((campaignType) => {
      this.campaignType.set(campaignType);
    });
  }

  private handleSelectedEmailStatusChanged(selectedEmailStatuses: string[]): void {
    const formattedStatues = this.handleWildCardInput(selectedEmailStatuses);
    if (selectedEmailStatuses?.length != formattedStatues?.length) {
      this.emailStatusFormControl.setValue(formattedStatues);
      return;
    }
  }

  private handleCampaignTypeChanged(campaignType: CampaignOwnerType | null): void {
    if (!campaignType) {
      return;
    }

    const options = GetEmailStatusOptions(campaignType);
    const selectedOptions = this.emailStatusFormControl.value || [];

    // to avoid showing "Sent" and "Send" at the same time use a "CombinedSent" option
    // if a yesware and internal campaign is selected
    if (
      campaignType == CampaignOwner.Any &&
      (selectedOptions.includes(MarketingEmailStatus.Sent) || selectedOptions.includes(YeswareEmailStatus.Send))
    ) {
      selectedOptions.push(EmailStatus.CombinedSent);
    }
    if (selectedOptions.includes(EmailStatus.CombinedSent)) {
      if (campaignType == CampaignOwner.Yesware) {
        selectedOptions.push(YeswareEmailStatus.Send);
      }
      if (campaignType == CampaignOwner.Marketing) {
        selectedOptions.push(MarketingEmailStatus.Sent);
      }
    }

    const filteredOptions = selectedOptions.filter((status) => options.has(status as EmailStatusType));
    if (filteredOptions.length !== selectedOptions.length) {
      this.emailStatusFormControl.setValue(filteredOptions);
    }
  }
}
